import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { CreateAgentRequest, ProviderInfo } from '../../types/agent.js';
import * as agentApi from '../../services/agent-api.js';

const PERSONALITY_TYPES = [
  { value: '', label: 'Kein Typ' },
  { value: 'coach', label: 'Coach' },
  { value: 'advisor', label: 'Berater' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'creative', label: 'Kreativ' },
  { value: 'assistant', label: 'Assistent' },
];

const DEFAULT_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6',
];

@customElement('cp-agent-form')
export class CpAgentForm extends LitElement {
  createRenderRoot() { return this; }

  @state() private _open = false;
  @state() private _name = '';
  @state() private _color = DEFAULT_COLORS[0];
  @state() private _personalityType = '';
  @state() private _provider = '';
  @state() private _providers: ProviderInfo[] = [];
  @state() private _modelsForProvider: string[] = [];
  @state() private _model = '';
  @state() private _submitting = false;
  @state() private _error: string | null = null;

  open(): void {
    this._resetForm();
    this._open = true;
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
    this._loadProviders();
  }

  private async _loadProviders(): Promise<void> {
    try {
      const res = await fetch('/api/providers', { credentials: 'include' });
      if (res.ok) {
        this._providers = await res.json() as ProviderInfo[];
        if (this._providers.length > 0 && !this._provider) {
          this._provider = this._providers[0].id;
          await this._loadModelsForProvider(this._provider);
        }
      }
    } catch { /* providers will be empty, dropdown stays hidden */ }
  }

  private async _loadModelsForProvider(providerId: string): Promise<void> {
    try {
      const res = await fetch(`/api/providers/${encodeURIComponent(providerId)}/models`, { credentials: 'include' });
      if (res.ok) {
        this._modelsForProvider = await res.json() as string[];
        if (this._modelsForProvider.length > 0 && !this._modelsForProvider.includes(this._model)) {
          this._model = this._modelsForProvider[0];
        }
      }
    } catch {
      this._modelsForProvider = [];
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  private _close(): void {
    this._open = false;
  }

  private _resetForm(): void {
    this._name = '';
    this._color = DEFAULT_COLORS[0];
    this._personalityType = '';
    this._model = '';
    this._submitting = false;
    this._error = null;
  }

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-agent-form__overlay')) {
      this._close();
    }
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._open) {
      this._close();
    }
  };

  private _onNameInput(e: Event): void {
    this._name = (e.target as HTMLInputElement).value;
    this._error = null;
  }

  private _onPersonalityChange(e: Event): void {
    this._personalityType = (e.target as HTMLSelectElement).value;
  }

  private _onColorSelect(color: string): void {
    this._color = color;
  }

  private _onColorInput(e: Event): void {
    this._color = (e.target as HTMLInputElement).value;
  }

  private async _onProviderChange(e: Event): Promise<void> {
    const newProvider = (e.target as HTMLSelectElement).value;
    this._provider = newProvider;
    await this._loadModelsForProvider(newProvider);
  }

  private _onModelChange(e: Event): void {
    this._model = (e.target as HTMLSelectElement).value;
  }

  private async _onSubmit(): Promise<void> {
    const name = this._name.trim();
    if (!name) {
      this._error = 'Name ist erforderlich';
      return;
    }
    if (this._submitting) return;

    this._submitting = true;
    this._error = null;

    try {
      const data: CreateAgentRequest = {
        name,
        color: this._color,
      };
      if (this._personalityType) {
        data.personality_type = this._personalityType;
      }
      if (this._provider) {
        data.provider = this._provider;
      }
      if (this._model) {
        data.model = this._model;
      }

      await agentApi.createAgent(data);
      this._close();
      this.dispatchEvent(new CustomEvent('agent-created', {
        bubbles: true,
        composed: true,
      }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Erstellen';
    } finally {
      this._submitting = false;
    }
  }

  render() {
    if (!this._open) return nothing;

    return html`
      <div class="cp-agent-form__overlay" @click=${this._onBackdropClick}>
        <div class="cp-agent-form__dialog">
          <div class="cp-agent-form__header">
            <h2 class="cp-agent-form__title">Neuer Agent</h2>
            <button class="cp-agent-form__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-agent-form__body">
            <div class="cp-agent-form__field">
              <label class="cp-agent-form__label">Name *</label>
              <input
                class="cp-agent-form__input"
                type="text"
                placeholder="z.B. Fitness Coach"
                .value=${this._name}
                @input=${(e: Event) => this._onNameInput(e)}
              />
            </div>

            <div class="cp-agent-form__field">
              <label class="cp-agent-form__label">Farbe</label>
              <div class="cp-agent-form__color-grid">
                ${DEFAULT_COLORS.map(c => html`
                  <button
                    class="cp-agent-form__color-swatch ${this._color === c ? 'cp-agent-form__color-swatch--selected' : ''}"
                    style="background-color: ${c}"
                    @click=${() => this._onColorSelect(c)}
                    type="button"
                  ></button>
                `)}
                <input
                  class="cp-agent-form__color-custom"
                  type="color"
                  .value=${this._color}
                  @input=${(e: Event) => this._onColorInput(e)}
                  title="Eigene Farbe"
                />
              </div>
            </div>

            <div class="cp-agent-form__field">
              <label class="cp-agent-form__label">Typ</label>
              <select
                class="cp-agent-form__select"
                .value=${this._personalityType}
                @change=${(e: Event) => this._onPersonalityChange(e)}
              >
                ${PERSONALITY_TYPES.map(t => html`
                  <option value=${t.value} ?selected=${this._personalityType === t.value}>${t.label}</option>
                `)}
              </select>
            </div>

            ${this._providers.length > 0 ? html`
              <div class="cp-agent-form__field">
                <label class="cp-agent-form__label">Provider</label>
                <select
                  class="cp-agent-form__select"
                  .value=${this._provider}
                  @change=${(e: Event) => void this._onProviderChange(e)}
                >
                  ${this._providers.map(p => html`
                    <option value=${p.id} ?selected=${this._provider === p.id}>${p.displayName}</option>
                  `)}
                </select>
              </div>
            ` : nothing}

            ${this._modelsForProvider.length > 0 ? html`
              <div class="cp-agent-form__field">
                <label class="cp-agent-form__label">Modell</label>
                <select
                  class="cp-agent-form__select"
                  .value=${this._model}
                  @change=${(e: Event) => this._onModelChange(e)}
                >
                  ${this._modelsForProvider.map(m => html`
                    <option value=${m} ?selected=${this._model === m}>${m}</option>
                  `)}
                </select>
              </div>
            ` : nothing}

            ${this._error ? html`
              <div class="cp-agent-form__error">${this._error}</div>
            ` : nothing}
          </div>

          <div class="cp-agent-form__footer">
            <button class="cp-agent-form__btn cp-agent-form__btn--cancel" @click=${this._close}>Abbrechen</button>
            <button
              class="cp-agent-form__btn cp-agent-form__btn--submit"
              ?disabled=${!this._name.trim() || this._submitting}
              @click=${() => void this._onSubmit()}
            >${this._submitting ? 'Erstellt...' : 'Erstellen'}</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-agent-form': CpAgentForm;
  }
}
